import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, UserPlus, UserCheck, UserMinus, MessageCircle, Rss, Camera, Pencil } from "lucide-react";
import api from "@/lib/api";
import { fileUrl, uploadFileChunked } from "@/lib/uploads";
import { compressImage } from "@/lib/imageCompress";
import { getInitials, avatarColor } from "@/lib/avatar";
import LiveRing from "@/components/live/LiveRing";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import PostCard from "@/components/social/PostCard";
import ReelsViewer from "@/components/social/ReelsViewer";
import HighlightsRow from "@/components/social/HighlightsRow";
import { toast } from "sonner";

function Stat({ n, label }) {
  return <div className="text-center"><div className="text-lg font-semibold">{n}</div><div className="text-xs text-[var(--rc-muted)]">{label}</div></div>;
}

export default function SocialProfile({ userId, onBack, onOpenProfile, onOpenThread, onCompose, refreshKey }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [p, setP] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reels, setReels] = useState(null); // fullscreen video pager — THIS user's videos only
  const avatarRef = useRef(null);
  const coverRef = useRef(null);

  // Only this person's videos, in order (no shuffle / no cross-feed mixing).
  const profileReels = React.useMemo(() => {
    const list = [];
    posts.forEach((post) => {
      const base = post.reshared || post;
      (base.media || []).forEach((m) => { if (m.type === "video") list.push({ post, url: m.url, poster: m.poster }); });
    });
    return list;
  }, [posts]);
  const openReel = (postId, url) => {
    const idx = profileReels.findIndex((r) => r.post.id === postId && r.url === url);
    setReels({ items: profileReels, idx: idx < 0 ? 0 : idx });
  };

  const load = () => {
    api.get(`/social/users/${userId}`).then(({ data }) => setP(data)).catch(() => {});
    api.get(`/social/users/${userId}/posts`).then(({ data }) => setPosts(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, refreshKey]);

  const addFriend = async () => { try { await api.post(`/social/friends/request/${userId}`); setP((x) => ({ ...x, friend_status: "requested" })); toast.success(t("s_request_sent")); } catch { toast.error(t("s_failed")); } };
  const acceptFriend = async () => { try { await api.post(`/social/friends/respond/${userId}`, { action: "accept" }); setP((x) => ({ ...x, friend_status: "friends", friends_count: x.friends_count + 1 })); toast.success(t("s_friend_added")); } catch {} };
  const unfriend = async () => { try { await api.delete(`/social/friends/${userId}`); setP((x) => ({ ...x, friend_status: "none", friends_count: Math.max(0, x.friends_count - 1) })); } catch {} };
  const follow = async () => { try { const { data } = await api.post(`/social/follow/${userId}`); setP((x) => ({ ...x, am_following: data.following, followers: x.followers + (data.following ? 1 : -1) })); } catch {} };
  const deleteSocial = async () => {
    if (!window.confirm(t("s_confirm_delete_social"))) return;
    try { await api.delete("/social/account"); setPosts([]); load(); toast.success(t("s_social_deleted")); } catch { toast.error(t("s_failed")); }
  };
  if (!p) return <div className="p-6 text-center text-[var(--rc-muted)]">{t("s_loading")}</div>;

  const uploadSocialImg = async (file, key) => {
    try { const small = await compressImage(file).catch(() => file); const { url } = await uploadFileChunked(small, null, "social");
      const { data } = await api.put("/social/profile", { [key]: url }); setP((x) => ({ ...x, ...data })); toast.success(t("ss_saved")); }
    catch { toast.error(t("s_could_not_upload")); }
  };
  const socialAv = p.social_avatar || p.avatar;

  const FriendBtn = () => {
    if (p.is_me) return null;
    if (p.friend_status === "friends") return <button onClick={unfriend} data-testid="profile-unfriend" className="flex items-center justify-center gap-2 rounded-full border border-[var(--rc-border)] text-[var(--rc-muted)] py-2.5 font-medium"><UserCheck size={17} /> {t("s_friends")}</button>;
    if (p.friend_status === "requested") return <button disabled className="flex items-center justify-center gap-2 rounded-full border border-[var(--rc-border)] text-[var(--rc-muted)] py-2.5 font-medium opacity-60"><UserMinus size={17} /> {t("s_requested")}</button>;
    if (p.friend_status === "incoming") return <button onClick={acceptFriend} data-testid="profile-accept" className="flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black py-2.5 font-semibold"><UserCheck size={17} /> {t("s_accept")}</button>;
    return <button onClick={addFriend} data-testid="profile-addfriend" className="flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black py-2.5 font-semibold"><UserPlus size={17} /> {t("s_add_friend")}</button>;
  };

  return (
    <div data-testid="social-profile">
      {onBack && <button onClick={onBack} data-testid="profile-back" className="flex items-center gap-1.5 text-[var(--rc-muted)] mb-3"><ArrowLeft size={18} /> {t("s_back")}</button>}
      <div className="rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] overflow-hidden text-center">
        {p.is_me && <>
          <input ref={coverRef} type="file" accept="image/*" hidden data-testid="social-cover-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadSocialImg(f, "social_cover"); }} />
          <input ref={avatarRef} type="file" accept="image/*" hidden data-testid="social-avatar-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadSocialImg(f, "social_avatar"); }} />
        </>}
        {/* Cover */}
        <div className="relative h-28 bg-[var(--rc-bg)]">
          {p.social_cover ? <img src={fileUrl(p.social_cover)} alt="" className="h-full w-full object-cover" data-testid="social-cover" /> : <div className="h-full w-full bg-gradient-to-br from-[var(--rc-gold)]/25 to-transparent" />}
          {p.is_me && <button onClick={() => coverRef.current?.click()} data-testid="edit-social-cover" className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/55 text-white"><Camera size={15} /></button>}
        </div>
        <div className="px-5 pb-5">
          {/* Avatar */}
          <div className="relative -mt-12 mx-auto w-24">
            <LiveRing uid={p.is_me ? null : p.id}>
              <div className="h-24 w-24 rounded-3xl grid place-items-center text-3xl text-[var(--rc-gold)] font-heading border-4 border-[var(--rc-surface)] overflow-hidden" style={{ backgroundColor: socialAv ? "var(--rc-bg)" : avatarColor(p.name) }} data-testid="social-profile-avatar">
                {socialAv ? <img src={fileUrl(socialAv)} alt="" className="h-full w-full object-cover" /> : getInitials(p.name)}
              </div>
            </LiveRing>
            {p.is_me && <button onClick={() => avatarRef.current?.click()} data-testid="edit-social-avatar" className="absolute bottom-0 right-0 h-8 w-8 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black border-2 border-[var(--rc-surface)]"><Camera size={14} /></button>}
          </div>
          <div className="mt-3 text-xl font-medium">{p.name} {p.flag}</div>
          {p.about && <div className="text-sm text-[var(--rc-muted)] mt-1">{p.about}</div>}
          <div className="flex items-center justify-center gap-8 mt-4">
            <Stat n={p.posts_count} label={t("s_posts")} />
            <Stat n={p.friends_count} label={t("s_friends")} />
            <Stat n={p.followers} label={t("s_followers")} />
            <Stat n={p.following} label={t("s_following_label")} />
          </div>
          {!p.is_me && (
            <div className="grid grid-cols-3 gap-2 mt-5">
              <FriendBtn />
              <button onClick={follow} data-testid="profile-follow" className={`flex items-center justify-center gap-2 rounded-full py-2.5 font-medium ${p.am_following ? "border border-[var(--rc-gold)]/40 text-[var(--rc-gold)]" : "border border-[var(--rc-border)] text-[var(--rc-muted)]"}`}><Rss size={16} /> {p.am_following ? t("s_following_label") : t("s_follow")}</button>
            <button onClick={() => onOpenThread({ id: p.id, name: p.name, avatar: p.avatar })} data-testid="profile-message" className="flex items-center justify-center gap-2 rounded-full border border-[var(--rc-border)] text-[var(--rc-muted)] py-2.5 font-medium"><MessageCircle size={16} /> {t("s_message")}</button>
          </div>
        )}
        </div>
      </div>

      <HighlightsRow userId={p.id} isMe={p.is_me} authorName={p.name} authorAvatar={socialAv} />

      <div className="mt-4 space-y-4">
        {p.is_me && onCompose && (
          <button onClick={onCompose} data-testid="profile-create-post"
            className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black py-3 font-semibold mb-4 hover:bg-[var(--rc-gold-light)] transition-colors">
            <Pencil size={17} /> {t("s_create_post") === "s_create_post" ? "Create post" : t("s_create_post")}
          </button>
        )}
        {posts.length ? posts.map((post) => <PostCard key={post.id} post={post} onOpenProfile={onOpenProfile} onOpenReel={openReel} onDeleted={(id) => setPosts((ps) => ps.filter((x) => x.id !== id))} />)
          : <div className="text-center py-10 text-[var(--rc-muted)]">{t("s_no_posts_yet")}</div>}
      </div>

      {reels && <ReelsViewer reels={reels.items} startIndex={reels.idx} onClose={() => setReels(null)} onOpenProfile={onOpenProfile} />}

      {p.is_me && (
        <div className="mt-8 rounded-2xl border border-red-500/25 p-4">
          <div className="text-sm font-medium text-red-400">{t("s_delete_my_data")}</div>
          <p className="text-xs text-[var(--rc-muted)] mt-1">{t("s_delete_my_data_desc")}</p>
          <button onClick={deleteSocial} data-testid="delete-social-account" className="mt-3 w-full rounded-xl border border-red-500/40 text-red-400 py-2.5 font-medium">{t("s_delete_my_data")}</button>
        </div>
      )}
    </div>
  );
}
